import React from "react";
import classnames from "classnames";

import "./index.scss";

export interface PillToggleOption<T extends string> {
  value: T;
  label: string;
  // Optional per-option count (review-tab-redesign-plan.md PR5 follow-up, Figma `9212:1400`'s Pending
  // pill). Renders as a separate `aria-hidden` sibling span, with the count folded into the BUTTON's own
  // `aria-label` instead -- the same shape TabBarNav.tsx already uses for its own tab badge, so a screen
  // reader gets an explicit "Pending, 7 items" rather than the digit silently becoming part of the
  // label's accessible name. Omitted or 0 renders nothing, so both of this component's pre-existing
  // callers (LeaderboardBoardToggle, and this same file's own Approved/Denied options) render identically
  // to before this prop existed.
  badge?: number;
}

interface PillToggleProps<T extends string> {
  options: PillToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  // Merged with the base class, never replacing it -- the same "restyle without rebuilding" precedent
  // LeaderboardBoardToggle's own `className` prop used before this extraction (its own doc comment,
  // now on the wrapper that calls this component).
  className?: string;
  // Merged onto every option button, alongside the base `pill-toggle-option` class. Lets a caller keep
  // its OWN option class name (LeaderboardBoardToggle passes "leaderboard-board-toggle-option") so the
  // external SCSS that already selects on it directly -- LeaderboardsContainer/index.scss's
  // `.leaderboard-tab-board-toggle .leaderboard-board-toggle-option` and OverviewLeaderboard/index.scss's
  // `.overview-leaderboard-toggle .leaderboard-board-toggle-option` -- keeps matching, unchanged, without
  // this shared component needing to know those callers exist.
  optionClassName?: string;
}

/**
 * The segmented pill control (SPARK/Review, Figma `9212:1400`): a row of buttons, one per option, the
 * current value's button marked `.selected`. Generic over the option value `T` so one control backs the
 * Review tab's Pending/Approved/Denied filter (PR4) and the Leaderboard tab's Received/Given toggle
 * alike, rather than each screen owning its own hard-typed copy -- `review-tab-redesign-plan.md`'s own
 * "No shared filter-pill / segmented control" gap.
 *
 * Modelled on `LeaderboardBoardToggle`, whose own `index.scss` was already exactly this pill
 * (`border-radius: 20px`, `$middle-gray-color`, `.selected` -> `$secondary`) -- that styling now lives
 * here as the base treatment, and `LeaderboardBoardToggle` wraps this component instead of duplicating
 * it (the plan's own "migrate, don't copy a third time").
 *
 * Presentational only -- no dispatch/select of its own; the caller owns `value` and `onChange`.
 *
 * @param props.options - the buttons to render, in order, each an explicit `{ value, label }` pair.
 * @param props.value - the currently selected option's value; compared against each option's own `value`.
 * @param props.onChange - called with the clicked option's own `value`, never its label.
 * @param props.className - optional modifier class, merged with the base class rather than replacing it.
 * @param props.optionClassName - optional modifier class, merged onto every option button.
 * @see PillToggleOption.badge for the optional per-option count badge.
 */
const PillToggle = <T extends string>(props: PillToggleProps<T>) => {
  const { options, value, onChange, className, optionClassName } = props;

  return (
    <div className={classnames("pill-toggle", className)}>
      {options.map((option) => {
        const hasBadge = Boolean(option.badge);

        return (
          <button
            key={option.value}
            type="button"
            className={classnames("pill-toggle-option", optionClassName, { selected: option.value === value })}
            onClick={() => onChange(option.value)}
            // Set ONLY when there is a badge -- a badge-less option keeps falling back to its own text
            // content as its accessible name, unchanged. Same `hasBadge` gate TabBarNav.tsx uses.
            aria-label={hasBadge ? `${option.label}, ${option.badge} items` : undefined}
          >
            {/* The label stays a bare text-node child, exactly as before this prop existed -- load-bearing
                for PillToggle.test.tsx's `getByText(label).className` assertions, which match the BUTTON
                itself only because getNodeText only reads direct text-node children. Wrapping the label in
                its own span (TabBarNav's own shape) would move that match onto the span instead and break
                every one of those assertions. The badge below is a sibling ELEMENT, so it never joins the
                button's own direct-text-node content. */}
            {option.label}
            {hasBadge && (
              <span className="pill-toggle-option-badge" aria-hidden="true">
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PillToggle;
